'use client';

import { useQuery } from '@tanstack/react-query';
import { fetchPlayerProfiles } from '@vimond/services/player-profile';
import { getEnvVar } from '@vimond/utils/env';

/**
 * Hook to fetch and cache player profiles globally.
 * This ensures player profiles are fetched once and shared across all video instances.
 */
export const usePlayerProfiles = () => {
  const { data: { data: playerProfiles } = {}, isLoading } = useQuery({
    queryKey: ['playerProfiles'],
    queryFn: fetchPlayerProfiles,
    enabled: getEnvVar('NEXT_PUBLIC_USE_PLAYER_PROFILE_SERVICE') === 'true',
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnMount: false, // Don't refetch on component mount if data exists
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch on reconnect
    retry: 1, // Only retry once on failure
  });

  const fallbackAdProfileId = getEnvVar('NEXT_PUBLIC_DEFAULT_AD_PROFILE_ID');

  /**
   * Get ad breaks for a specific ad profile ID.
   * Falls back to default ad profile if the specified profile is not found.
   */
  const getAdBreaksForProfile = (adProfileId?: string) => {
    if (!playerProfiles || !adProfileId) return undefined;

    return (
      playerProfiles.find((p) => p.id === adProfileId)?.adBreaks ??
      playerProfiles.find(
        (p) => !!fallbackAdProfileId && p.id === fallbackAdProfileId,
      )?.adBreaks
    );
  };

  return {
    playerProfiles,
    isLoading,
    getAdBreaksForProfile,
  };
};
